import React, { useState, useEffect } from 'react';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { api } from '../../api/nocode';

const DEFAULT_FIRST_CTA = {
  headline: 'All-Access Pass',
  icon: 'Star',
  copy: 'Keep your camp experience for good. Upgrade for $19 to retain your Compass, workbook answers, camp resources, and replay access.',
  buttonLabel: 'Upgrade to All-Access — $19',
  buttonUrl: '/upgrade'
};

const DEFAULT_SECOND_CTA = {
  headline: 'Faith-Based Book Writing Month',
  icon: 'Calendar',
  copy: 'Join Christian writers from around the world for Faith-Based Book Writing Month — a focused month to make meaningful progress on the book God has placed on your heart.',
  buttonLabel: 'Join Now',
  buttonUrl: 'https://www.faibowrimo.com/join'
};

function ExpiryCtaSettings({ title, description, icon, values, defaults, prefix, onChange }) {
  return (
    <Card className="space-y-6">
      <div className="border-b pb-2">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <SafeIcon name={icon} /> {title}
        </h2>
        <p className="text-xs text-muted mt-1">{description}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Headline"
          value={values[`${prefix}_headline`] || defaults.headline}
          onChange={(event) => onChange(`${prefix}_headline`, event.target.value)}
        />
        <Input
          label="Icon Name"
          value={values[`${prefix}_icon`] || defaults.icon}
          onChange={(event) => onChange(`${prefix}_icon`, event.target.value)}
          placeholder={defaults.icon}
        />
      </div>

      <Textarea
        label="Module Copy"
        value={values[`${prefix}_copy`] || defaults.copy}
        onChange={(event) => onChange(`${prefix}_copy`, event.target.value)}
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="CTA Button Name"
          value={values[`${prefix}_button_label`] || defaults.buttonLabel}
          onChange={(event) => onChange(`${prefix}_button_label`, event.target.value)}
        />
        <Input
          label="CTA Button Link"
          type="url"
          value={values[`${prefix}_button_url`] || defaults.buttonUrl}
          onChange={(event) => onChange(`${prefix}_button_url`, event.target.value)}
          placeholder={defaults.buttonUrl}
        />
      </div>
    </Card>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const regions = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1',
    'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1'
  ];

  const loadSettings = async () => {
    try {
      const res = await api.get('settings_gwacamp');
      const data = Array.isArray(res) ? res : res?.data || [];
      setOriginalSettings(data);

      const map = {};
      data.forEach((item) => {
        if (item?.setting_key) map[item.setting_key] = item.value;
      });

      setSettings(map);
    } catch (error) {
      console.error('Failed to load settings', error);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (key, value) => {
    setSettings((previous) => ({ ...previous, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setToast('');

    try {
      const requests = [];

      for (const [key, value] of Object.entries(settings)) {
        const original = originalSettings.find((setting) => setting?.setting_key === key);
        const stringValue = String(value);

        if (original?.id) {
          if (original.value !== stringValue) {
            requests.push(api.put('settings_gwacamp', original.id, {
              setting_key: key,
              value: stringValue,
              owner_id: 'admin'
            }));
          }
        } else if (stringValue !== '') {
          requests.push(api.post('settings_gwacamp', {
            setting_key: key,
            value: stringValue,
            owner_id: 'admin'
          }));
        }
      }

      await Promise.all(requests);
      setToast('Settings saved successfully!');
      await loadSettings();
    } catch (error) {
      console.error(error);
      setToast('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
      window.setTimeout(() => setToast(''), 3000);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Platform Settings</h1>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      {toast && (
        <div className={`px-4 py-3 rounded border ${toast.includes('Error') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'}`}>
          {toast}
        </div>
      )}

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Users" /> Public Registration
        </h2>
        <div className="p-4 border rounded-md bg-gray-50 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-dark-green">Enable Registration</h4>
            <p className="text-xs text-muted">When OFF, the registration page will show a closed message and redirect to the waitlist.</p>
          </div>
          <Button
            variant={settings.registration_open !== 'false' ? 'primary' : 'outline'}
            onClick={() => handleChange('registration_open', settings.registration_open === 'false' ? 'true' : 'false')}
            className={settings.registration_open !== 'false' ? 'bg-forest-green text-white border-forest-green' : 'text-gray-500'}
          >
            {settings.registration_open !== 'false' ? 'ON' : 'OFF'}
          </Button>
        </div>
      </Card>

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Mail" /> Self-Letter Writing Module
        </h2>
        <div className="p-4 border rounded-md bg-gray-50 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-dark-green">Enable Self-Letter Module</h4>
            <p className="text-xs text-muted">When OFF, the letters module will be locked for all campers.</p>
          </div>
          <Button
            variant={settings.letters_module_open !== 'false' ? 'primary' : 'outline'}
            onClick={() => handleChange('letters_module_open', settings.letters_module_open === 'false' ? 'true' : 'false')}
            className={settings.letters_module_open !== 'false' ? 'bg-forest-green text-white border-forest-green' : 'text-gray-500'}
          >
            {settings.letters_module_open !== 'false' ? 'ON' : 'OFF'}
          </Button>
        </div>
      </Card>

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="MessageSquare" /> Dashboard Messages
        </h2>
        <div className="space-y-4">
          <Textarea
            label="Camp Closed Message (All Campers)"
            value={settings.camp_closed_msg || ''}
            onChange={(event) => handleChange('camp_closed_msg', event.target.value)}
            placeholder="Message displayed to all campers after camp ends, until replay expires."
          />
          <Textarea
            label="All-Access Welcome Message (Post-Camp)"
            value={settings.aa_welcome_msg || ''}
            onChange={(event) => handleChange('aa_welcome_msg', event.target.value)}
            placeholder="Message displayed to All-Access members after camp ends if Camp Closed Message is empty."
          />
          <Textarea
            label="Replay Expiry Message (Lockout Screen)"
            value={settings.replay_expiry_msg || ''}
            onChange={(event) => handleChange('replay_expiry_msg', event.target.value)}
            placeholder="Message displayed to free pass users when replay access has expired."
          />
        </div>
      </Card>

      <ExpiryCtaSettings
        title="Expiry Screen: First CTA Module"
        description="This All-Access upgrade module appears first after a free camper's replay access expires. Its default CTA is orange, changing to white with orange text on hover."
        icon="Star"
        values={settings}
        defaults={DEFAULT_FIRST_CTA}
        prefix="expiry_first_cta"
        onChange={handleChange}
      />

      <ExpiryCtaSettings
        title="Expiry Screen: Second CTA Module"
        description="This module appears beside the All-Access upgrade offer after a free camper's replay access expires."
        icon="Calendar"
        values={settings}
        defaults={DEFAULT_SECOND_CTA}
        prefix="expiry_second_cta"
        onChange={handleChange}
      />

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Mail" /> AWS SES Configuration
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="AWS Access Key" type="text" value={settings.ses_access_key || ''} onChange={(event) => handleChange('ses_access_key', event.target.value)} />
          <Input label="AWS Secret Key" type="password" value={settings.ses_secret_key || ''} onChange={(event) => handleChange('ses_secret_key', event.target.value)} />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-dark-green">AWS Region</label>
            <select
              className="px-4 py-2 rounded-md border border-amber bg-cream focus:outline-none focus:ring-2 focus:ring-forest-green"
              value={settings.ses_region || 'us-east-1'}
              onChange={(event) => handleChange('ses_region', event.target.value)}
            >
              {regions.map((region) => <option key={region} value={region}>{region}</option>)}
            </select>
          </div>
          <Input label="From Email" value={settings.ses_from_email || ''} onChange={(event) => handleChange('ses_from_email', event.target.value)} />
          <Input label="Display Name" value={settings.ses_display_name || ''} onChange={(event) => handleChange('ses_display_name', event.target.value)} />
        </div>
      </Card>

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Link" /> Skillplate Webhook Configuration
        </h2>
        <div className="space-y-4">
          <Input label="Webhook Secret Key" type="text" value={settings.webhook_secret || ''} onChange={(event) => handleChange('webhook_secret', event.target.value)} className="font-mono text-sm" />
          <Input label="Skillplate Product Name" value={settings.skillplate_product || 'Camp FaiBoWriMo 2026 All-Access Pass'} onChange={(event) => handleChange('skillplate_product', event.target.value)} />
          <Input
            label="Webhook URL (Copy to Skillplate)"
            value={`https://assets.emmausway.co/camp_fbwm/webhook-receiver.php?secret=${settings.webhook_secret || ''}`}
            readOnly
            className="bg-gray-50 font-mono text-xs"
          />
          <p className="text-xs text-muted">Paste this exact URL into your Skillplate webhook settings. It contains the secret required to authenticate requests.</p>
        </div>
      </Card>

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Calendar" /> Camp Dates Configuration
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Camp Start Date" type="date" value={settings.camp_start || ''} onChange={(event) => handleChange('camp_start', event.target.value)} />
          <Input label="Camp End Date" type="date" value={settings.camp_end || ''} onChange={(event) => handleChange('camp_end', event.target.value)} />
          <Input label="Early Bird Deadline" type="datetime-local" value={settings.early_bird_deadline || ''} onChange={(event) => handleChange('early_bird_deadline', event.target.value)} />
          <Input label="Replay Expiry (Live Camp)" type="datetime-local" value={settings.replay_expiry || ''} onChange={(event) => handleChange('replay_expiry', event.target.value)} />
          <Input label="Evergreen Replay Window (Days)" type="number" value={settings.evergreen_replay_days || '14'} onChange={(event) => handleChange('evergreen_replay_days', event.target.value)} />
        </div>
      </Card>
    </div>
  );
}